import { Table, TableBody, TableCell, TableRow, TableHead } from '@mui/material';
import styled from 'styled-components';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import TableToolbar from 'components/TableToolbar';
import EmptyTableContent from 'components/EmptyTableContent';
import { fetchIntegrationServerQueuesStatus } from 'thunk';
import { removeUnderscoresAndCapitalizeFirstLetterOfEachWord } from 'utils';
import { selectIntegrationServerQueuesStatusData } from 'selectors';

const TableContainer = styled.div`
  .head-cell,
  .cell {
    &:nth-child(1) {
      width: 100%;
      max-width: 100%;
      word-break: break-all;
    }
    width: 20%;
    max-width: 20%;
  }
  .empty-content-wrapper {
    padding-bottom: 3rem;
  }
  .row-height {
    height: 7.7rem;
  }
  .queue-column {
    min-width: 10rem;
  }
`;

const headCells = [
  {
    id: 'queue',
    label: 'Queue',
  },
  {
    id: 'active',
    label: 'Active',
  },
  {
    id: 'completed',
    label: 'Completed',
  },
  {
    id: 'delayed',
    label: 'Delayed',
  },
  {
    id: 'paused',
    label: 'Paused',
  },
  {
    id: 'failed',
    label: 'Failed',
  },
  {
    id: 'waiting',
    label: 'Waiting',
  },
];
const noop = () => {};

const FETCH_ERROR_MESSAGE =
  'Unable to load queue status. Please check your connection and try again.';
const NO_DATA_MESSAGE = 'No Queues Found';

const useIntegrationServerQueuesStatusTable = () => {
  const dispatch = useAppDispatch();
  const { data, isRequesting, error } = useAppSelector(selectIntegrationServerQueuesStatusData);

  useEffect(() => {
    dispatch(fetchIntegrationServerQueuesStatus());
  }, [dispatch]);

  const queuesName = data ? Object.keys(data) : [];

  const renderTableToolbar = () => <TableToolbar hideFilterIcon tableTitle="Queues Status" />;

  const renderTableHead = () => (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} className="head-cell queue-column" align="left">
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  const renderCell = (queue: string, headCellId: string, key?: string) => {
    if (!data) return null;

    const isQueueNameCell = headCellId === 'queue';

    if (isQueueNameCell) {
      const firstUnderscoreIndex = queue.indexOf('_');
      const isFirstWordANumber = !isNaN(parseInt(queue[0]));

      return (
        <TableCell key={key} className="cell queue-column">
          {isFirstWordANumber
            ? removeUnderscoresAndCapitalizeFirstLetterOfEachWord(queue).slice(
                firstUnderscoreIndex + 1,
              )
            : removeUnderscoresAndCapitalizeFirstLetterOfEachWord(queue)}
        </TableCell>
      );
    }

    const queueData = data[queue][1];

    return <TableCell key={key}>{queueData[headCellId]}</TableCell>;
  };

  const renderRows = () => {
    return queuesName.map((queue: string) => (
      <TableRow hover onClick={noop} tabIndex={-1} key={queue} className="row-height">
        {headCells.map((headCell) => renderCell(queue, headCell.id, headCell.id))}
      </TableRow>
    ));
  };

  const renderTableBody = () => {
    if (!error?.message && !isRequesting) return <TableBody>{renderRows()}</TableBody>;
  };
  const renderTable = () => (
    <TableContainer>
      <Table>
        {renderTableHead()}
        {renderTableBody()}
      </Table>
      {!error?.message && !isRequesting && queuesName.length === 0 && (
        <EmptyTableContent message={NO_DATA_MESSAGE} />
      )}
      {error?.message && <EmptyTableContent message={FETCH_ERROR_MESSAGE} />}
    </TableContainer>
  );

  return {
    queuesStatus: data,
    renderTableToolbar,
    renderTable,
    isQueuesStatusTableLoading: isRequesting,
    queuesStatusTableError: error,
  };
};

export default useIntegrationServerQueuesStatusTable;
